<template>
  <b-modal
    v-model="modal"
    size="lg"
    dialog-class="rounded-[.375rem]"
    body-class="!px-14 !py-[3rem]"
    hide-header
    hide-footer
    centered
    lazy
  >
    <i
      class="fa fa-close z-[50] absolute top-[15px] right-[17px] text-[16px] text-[#6E747A] transition cursor-pinter hover:opacity-60"
      @click="modal = false"
    />

    <template v-if="!success">
      <b-form
        class="w-full flex flex-col items-center"
        @submit.prevent="importPagesToProject"
      >
        <h4 class="text-center text-[27px] font-semibold text-[#1D1D1D] mb-12">
          Add to Existing Project
        </h4>

        <b-form-group
          label="Which project would you like to add this to?"
          class="w-full mb-4"
        >
          <v-select 
            v-model="selectedProject"
            :filterable="false"
            :options="projects"
            class="custom-select"
            drop-down-classes="add-to-project-dropdown"
            label="name"
            @close="onClose"
            @open="onOpen"
          >
            <template #option="{ name, domain, subdomain }">
              <div class="flex flex-col">
                {{ name }}
                <span class="text-xs text-[#6E747A]">
                  {{ domain || `${subdomain}.estage.site` }}
                </span>
              </div>
            </template>
            <template #list-footer>
              <li
                v-show="hasNextPage"
                ref="load"
                class="loader"
              >
                <!--            Loading more options...-->
              </li>
            </template>
          </v-select>
        </b-form-group>

        <div class="w-full">
          <h3 class="text-xl text-black font-semibold mb-0.5">
            {{ template.name }}
          </h3>
          <p class="text-base text-[#3C4043] mb-4">
            Select the pages you would like to add.
          </p>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-4 w-full gap-y-4 gap-x-8 mb-8">
          <div
            v-for="page in template.pages"
            :key="page.id"
          >
            <div
              class="border relative w-full aspect-square rounded bg-[#c9cfd7] overflow-hidden bg-cover bg-top mb-2"
              :style="{ backgroundImage: `url(${page.cover_url || page.cover})` }"
            />
            <div class="flex items-baseline">
              <b-checkbox 
                :value="false"
                @change="addPage(page)"
              />
              <label class="text-sm text-[#6E747A]">
                {{ page.name }}
              </label>
            </div>
          </div>
        </div>

        <button
          class="inline-flex items-center justify-center text-decoration-none hover:bg-[#6795EB] text-base font-semibold text-white !px-[11px] rounded bg-[#4F83E3] h-12 w-full lg:w-[180px] opacity-50"
          :class="{'!opacity-100': !disableSubmit && !loading}"
          :disabled="disableSubmit || loading"
        >
          Add To Project
          <b-spinner
            v-if="loading"
            small
            variant="white"
            class="ml-2"
          />
        </button>
      </b-form>
    </template>
    <template v-else>
      <div class="w-full flex flex-col items-center">
        <svg
          width="103"
          height="104"
          viewBox="0 0 103 104"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="mb-2 w-[120px] h-[120px]"
        >
          <g clip-path="url(#clip0_14169_9634)">
            <path
              d="M27.5987 100.047C27.2247 100.047 26.8507 99.9064 26.561 99.6253C25.9704 99.0521 25.9563 98.1088 26.5303 97.5183L28.3821 95.6105C28.9554 95.02 29.8986 95.0058 30.4891 95.579C31.0797 96.1522 31.0938 97.0955 30.5206 97.686L28.668 99.5938C28.3759 99.8954 27.9877 100.047 27.5987 100.047Z"
              fill="#FF4E61"
            />
            <path
              d="M47.7002 26.861C47.327 26.861 46.9522 26.7208 46.6633 26.4397C46.0727 25.8665 46.0586 24.9233 46.6318 24.3335L48.4844 22.425C49.0576 21.8344 50.0001 21.8203 50.5906 22.3935C51.1812 22.9667 51.1953 23.9099 50.6221 24.5005L48.7695 26.409C48.4773 26.7098 48.0892 26.861 47.7002 26.861Z"
              fill="#FF4E61"
            />
            <path
              d="M90.4636 20.2353C90.0896 20.2353 89.7156 20.0959 89.4258 19.8148C88.8353 19.2416 88.8211 18.2984 89.3943 17.7078L91.247 15.7993C91.8202 15.2095 92.7635 15.1954 93.354 15.7686C93.9445 16.341 93.9587 17.285 93.3855 17.8748L91.5328 19.7833C91.2407 20.0841 90.8525 20.2353 90.4636 20.2353Z"
              fill="#5C73BC"
            />
            <path
              d="M90.4636 93.4808C90.0896 93.4808 89.7156 93.3407 89.4258 93.0604C88.8353 92.4872 88.8211 91.5439 89.3943 90.9534L91.247 89.0448C91.8202 88.4551 92.7635 88.4401 93.354 89.0133C93.9445 89.5865 93.9587 90.5298 93.3855 91.1203L91.5328 93.0289C91.2407 93.3296 90.8525 93.4808 90.4636 93.4808Z"
              fill="#FFAA00"
            />
            <path
              d="M19.7676 22.7587C19.3944 22.7587 19.0196 22.6194 18.7306 22.3383C18.1401 21.7651 18.1259 20.8218 18.6991 20.2313L20.5518 18.3227C21.125 17.733 22.0683 17.7188 22.6588 18.292C23.2485 18.8652 23.2627 19.8085 22.6895 20.3982L20.8368 22.3068C20.5447 22.6076 20.1566 22.7587 19.7676 22.7587Z"
              fill="#FFAA00"
            />
            <path
              d="M9.53578 56.2013C9.16257 56.2013 8.78778 56.062 8.49882 55.7809C7.9083 55.2077 7.89412 54.2644 8.46733 53.6739L10.3192 51.7653C10.8924 51.1748 11.8357 51.1606 12.4262 51.7338C13.0167 52.307 13.0309 53.2503 12.4577 53.8408L10.605 55.7494C10.3137 56.0501 9.92474 56.2013 9.53578 56.2013Z"
              fill="#2DC471"
            />
            <path
              d="M50.652 56.1345L45.8152 58.7541C44.5609 59.4336 43.0949 58.336 43.3342 56.8967L44.2578 51.3489C44.3531 50.7773 44.1696 50.1947 43.7665 49.7899L39.8533 45.8602C38.8384 44.8414 39.3982 43.0658 40.8013 42.8556L46.2081 42.0462C46.7656 41.9627 47.2466 41.6029 47.4954 41.0825L49.9142 36.0347C50.5418 34.7261 52.3535 34.7261 52.981 36.0347L55.399 41.0825C55.6486 41.6029 56.1297 41.9627 56.6864 42.0462L62.094 42.8556C63.4963 43.0658 64.0561 44.8414 63.0412 45.8602L59.1288 49.7899C58.7256 50.1947 58.5414 50.7773 58.6367 51.3489L59.5602 56.8967C59.8004 58.336 58.3343 59.4336 57.08 58.7541L52.2433 56.1345C51.7449 55.8645 51.1496 55.8645 50.652 56.1345Z"
              fill="#FFD02F"
            />
            <path
              d="M51.4472 103.202C50.6244 103.202 49.9575 102.535 49.9575 101.712V78.4124C49.9575 77.5896 50.6244 76.9219 51.4472 76.9219C52.27 76.9219 52.9369 77.5896 52.9369 78.4124V101.712C52.9369 102.535 52.27 103.202 51.4472 103.202Z"
              fill="#5C73BC"
            />
            <path
              d="M51.4591 72.0441H51.4363C50.6135 72.0441 49.9458 71.3765 49.9458 70.5537C49.9458 69.7309 50.6127 69.064 51.4363 69.064H51.4591C52.2819 69.064 52.9488 69.7309 52.9488 70.5537C52.9488 71.3765 52.2819 72.0441 51.4591 72.0441Z"
              fill="#5C73BC"
            />
            <path
              d="M51.459 66.9188C51.4566 66.9188 51.4543 66.9188 51.4527 66.9188H51.4291C50.6063 66.9157 49.9425 66.2456 49.9457 65.4228C49.9496 64.6016 50.6157 63.9395 51.4354 63.9395H51.4425H51.4653C52.2881 63.9426 52.9519 64.6127 52.9487 65.4354C52.9456 66.2559 52.2787 66.9188 51.459 66.9188Z"
              fill="#FFAA00"
            />
            <path
              d="M24.6383 46.5406C24.2493 46.5406 23.8611 46.3894 23.569 46.0887L7.57684 29.6131C7.00364 29.0225 7.01702 28.0793 7.60755 27.5068C8.19807 26.9336 9.14134 26.9478 9.71454 27.5376L25.7075 44.0132C26.2807 44.6029 26.2665 45.547 25.676 46.1194C25.3863 46.4005 25.0123 46.5406 24.6383 46.5406Z"
              fill="#FF4E61"
            />
            <path
              d="M30.0238 52.1055C29.6482 52.1055 29.2727 51.9646 28.9829 51.6811C28.394 51.1063 28.3829 50.1631 28.9577 49.5741L28.9743 49.5576C29.549 48.9694 30.4923 48.9576 31.0812 49.5332C31.6702 50.108 31.6812 51.0512 31.1057 51.6402L31.0899 51.6567C30.7978 51.9551 30.4112 52.1055 30.0238 52.1055Z"
              fill="#FFAA00"
            />
            <path
              d="M33.541 55.7291C33.167 55.7291 32.793 55.5889 32.5033 55.3079C31.9135 54.7347 31.8993 53.7914 32.4725 53.2017L32.4883 53.1843C33.0623 52.5938 34.0055 52.5804 34.5953 53.1536C35.1858 53.7268 35.2 54.6701 34.6268 55.2606L34.6102 55.2771C34.3181 55.5779 33.93 55.7291 33.541 55.7291Z"
              fill="#5C73BC"
            />
            <path
              d="M78.2562 45.9546C77.8822 45.9546 77.5082 45.8152 77.2193 45.5341C76.6288 44.9609 76.6146 44.0177 77.1878 43.4271L93.18 26.9523C93.7532 26.3618 94.6964 26.3476 95.287 26.9208C95.8775 27.494 95.8917 28.4373 95.3185 29.0278L79.3255 45.5026C79.0334 45.8034 78.6452 45.9546 78.2562 45.9546Z"
              fill="#FFAA00"
            />
            <path
              d="M72.8707 51.5205C72.4849 51.5205 72.0999 51.3717 71.8086 51.0749L71.792 51.0583C71.2149 50.4709 71.2235 49.5277 71.8101 48.9513C72.3975 48.3742 73.3408 48.3828 73.9171 48.9694L73.9337 48.986C74.51 49.5733 74.5021 50.5166 73.9148 51.093C73.625 51.378 73.2479 51.5205 72.8707 51.5205Z"
              fill="#FF4E61"
            />
            <path
              d="M69.3535 55.1438C68.9645 55.1438 68.5763 54.9927 68.2842 54.6919L68.2677 54.6754C67.6953 54.0848 67.7087 53.1416 68.2992 52.5684C68.8897 51.9952 69.833 52.0093 70.4062 52.5991L70.4227 52.6156C70.9951 53.2061 70.9817 54.1494 70.3912 54.7226C70.1015 55.0037 69.7275 55.1438 69.3535 55.1438Z"
              fill="#FFAA00"
            />
            <path
              d="M67.6559 23.521C67.4055 23.521 67.152 23.4581 66.9197 23.325C66.2048 22.9171 65.956 22.0069 66.3638 21.2928L77.8255 1.20632C78.2334 0.492179 79.1428 0.243372 79.8577 0.651227C80.5726 1.05908 80.8215 1.96928 80.4136 2.68342L68.9519 22.7699C68.6771 23.251 68.174 23.521 67.6559 23.521Z"
              fill="#62D38F"
            />
            <path
              d="M63.8002 30.3012C63.5396 30.3012 63.2766 30.2335 63.0365 30.0902L63.0168 30.0784C62.3113 29.6564 62.0806 28.7414 62.5034 28.0352C62.9254 27.3281 63.8404 27.099 64.5466 27.521L64.5663 27.5328C65.2726 27.9549 65.5025 28.8698 65.0805 29.5761C64.8009 30.043 64.3073 30.3012 63.8002 30.3012Z"
              fill="#FFAA00"
            />
            <path
              d="M61.2791 34.719C61.0169 34.719 60.7515 34.6497 60.5114 34.5048L60.4917 34.493C59.787 34.0678 59.5602 33.1521 59.9846 32.4474C60.4098 31.7427 61.3255 31.5167 62.0302 31.9411L62.0499 31.9529C62.7546 32.3781 62.9813 33.2938 62.5562 33.9985C62.2766 34.4623 61.7838 34.719 61.2791 34.719Z"
              fill="#2DC471"
            />
            <path
              d="M35.2669 23.5212C34.7489 23.5212 34.2457 23.2511 33.9709 22.7693L22.5093 2.68359C22.1014 1.96866 22.3502 1.05925 23.0644 0.651396C23.7793 0.243541 24.6895 0.491561 25.0973 1.20649L36.559 21.2922C36.9669 22.0071 36.7181 22.9173 36.0039 23.3252C35.7709 23.4582 35.5173 23.5212 35.2669 23.5212Z"
              fill="#5C73BC"
            />
            <path
              d="M39.1227 30.3012C38.6172 30.3012 38.1235 30.0438 37.844 29.5792C37.4204 28.8738 37.6487 27.958 38.3534 27.5344L38.3739 27.5226C39.0794 27.099 39.9943 27.3266 40.4179 28.0321C40.8423 28.7375 40.6139 29.6525 39.9085 30.0768L39.8888 30.0887C39.6486 30.2327 39.3841 30.3012 39.1227 30.3012Z"
              fill="#FF4E61"
            />
            <path
              d="M41.6438 34.719C41.1399 34.719 40.6486 34.4639 40.3683 34.0009C39.9415 33.297 40.1667 32.3813 40.8698 31.9545L40.8895 31.9427C41.5942 31.5168 42.5099 31.7412 42.9367 32.4451C43.3626 33.149 43.1382 34.0647 42.4343 34.4914L42.4146 34.5032C42.1729 34.6497 41.9068 34.719 41.6438 34.719Z"
              fill="#FFAA00"
            />
            <path
              d="M88.1133 72.0155L84.9843 74.354C84.1733 74.961 83.0387 74.3453 83.0647 73.3131L83.1663 69.3322C83.1765 68.9219 82.9907 68.533 82.6686 68.2913L79.5428 65.9488C78.7318 65.341 78.9491 64.0395 79.9105 63.7458L83.6174 62.6151C83.9985 62.4986 84.3008 62.1955 84.4237 61.8057L85.6205 58.0193C85.9307 57.0367 87.1999 56.8477 87.7676 57.6988L89.9573 60.9814C90.1833 61.3191 90.5557 61.5215 90.9533 61.5215L94.8193 61.5238C95.8224 61.5246 96.3893 62.7096 95.7783 63.5293L93.4248 66.6882C93.1823 67.0141 93.1107 67.4417 93.2335 67.8314L94.4264 71.6202C94.735 72.6029 93.8162 73.5241 92.8713 73.1792L89.2274 71.8494C88.8526 71.7124 88.4361 71.7746 88.1133 72.0155Z"
              fill="#FFD02F"
            />
            <path
              d="M59.7658 103.202C58.943 103.202 58.2761 102.535 58.2761 101.712C58.2761 90.1764 62.8783 82.3775 66.7387 77.8659C70.937 72.9614 75.1265 70.8064 75.3029 70.7166C76.0367 70.3442 76.9335 70.6379 77.3052 71.3725C77.6768 72.1056 77.3847 73.0008 76.6517 73.374C76.5887 73.4063 72.7211 75.4227 68.9182 79.9036C63.8333 85.8939 61.2555 93.2314 61.2555 101.712C61.2555 102.535 60.5886 103.202 59.7658 103.202Z"
              fill="#FF4E61"
            />
            <path
              d="M27.7869 80.0926L30.1585 83.2382C30.7742 84.0539 32.0301 83.7885 32.2844 82.7894L33.2646 78.9368C33.3654 78.5407 33.6497 78.2203 34.0244 78.0809L37.6605 76.729C38.6038 76.3786 38.7471 75.0661 37.9038 74.5086L34.6504 72.3575C34.3158 72.1355 34.1079 71.7584 34.0945 71.3489L33.9701 67.368C33.9386 66.3358 32.771 65.7893 31.9954 66.4436L29.0042 68.9664C28.6956 69.2262 28.283 69.3136 27.9011 69.1994L24.1879 68.0908C23.2249 67.8034 22.3596 68.7782 22.7242 69.7403L24.1288 73.4512C24.2737 73.8331 24.2265 74.2645 24.0029 74.6039L21.8321 77.899C21.2691 78.7541 21.9014 79.9021 22.9029 79.843L26.7618 79.6131C27.1594 79.5895 27.5429 79.769 27.7869 80.0926Z"
              fill="#FFD02F"
            />
            <path
              d="M43.1398 103.202C42.317 103.202 41.6501 102.535 41.6501 101.712C41.6501 95.6532 40.3037 90.1102 37.6487 85.2372C37.255 84.5144 37.5219 83.6097 38.2447 83.216C38.9675 82.8223 39.8722 83.0892 40.2659 83.8112C43.1611 89.1275 44.6295 95.1501 44.6295 101.712C44.6295 102.535 43.9626 103.202 43.1398 103.202Z"
              fill="#FFAA00"
            />
            <path
              d="M61.261 11.2671C61.261 8.99245 59.4705 7.14844 57.2628 7.14844C55.0542 7.14844 53.2645 8.99245 53.2645 11.2671C53.2645 13.5418 55.0542 15.3866 57.2628 15.3866C59.4705 15.3866 61.261 13.5418 61.261 11.2671Z"
              fill="#FFD02F"
            />
            <path
              d="M82.2545 90.0621C82.2545 87.7874 80.4648 85.9434 78.257 85.9434C76.0485 85.9434 74.2588 87.7874 74.2588 90.0621C74.2588 92.3368 76.0485 94.1808 78.257 94.1808C80.4648 94.1808 82.2545 92.3368 82.2545 90.0621Z"
              fill="#5C73BC"
            />
            <path
              d="M15.9497 90.8772C15.9497 89.0529 14.5143 87.5742 12.7428 87.5742C10.972 87.5742 9.53662 89.0529 9.53662 90.8772C9.53662 92.7023 10.972 94.181 12.7428 94.181C14.5143 94.181 15.9497 92.7023 15.9497 90.8772Z"
              fill="#62D38F"
            />
            <path
              d="M24.8272 58.1218C24.8272 56.2975 23.3918 54.8188 21.6202 54.8188C19.8494 54.8188 18.4141 56.2975 18.4141 58.1218C18.4141 59.9462 19.8494 61.4256 21.6202 61.4256C23.3918 61.4256 24.8272 59.9462 24.8272 58.1218Z"
              fill="#5C73BC"
            />
            <path
              d="M49.5529 3.30379C49.5529 1.47867 48.1175 0 46.3467 0C44.5751 0 43.1398 1.47867 43.1398 3.30379C43.1398 5.12811 44.5751 6.60678 46.3467 6.60678C48.1175 6.60678 49.5529 5.12811 49.5529 3.30379Z"
              fill="#FF4E61"
            />
            <path
              d="M80.6719 30.816C80.6719 28.9917 79.2358 27.5122 77.465 27.5122C75.6942 27.5122 74.2581 28.9917 74.2581 30.816C74.2581 32.6403 75.6942 34.119 77.465 34.119C79.2358 34.119 80.6719 32.6403 80.6719 30.816Z"
              fill="#FF4E61"
            />
            <path
              d="M35.2653 6.60073C34.8913 6.60073 34.5173 6.46137 34.2276 6.17949C33.6371 5.60629 33.6229 4.66381 34.1961 4.07329L36.0488 2.16471C36.622 1.57419 37.5652 1.56002 38.155 2.13322C38.7455 2.70642 38.7597 3.64969 38.1865 4.24021L36.3346 6.14878C36.0425 6.44956 35.6535 6.60073 35.2653 6.60073Z"
              fill="#FF4E61"
            />
          </g>
          <defs>
            <clipPath id="clip0_14169_9634">
              <rect
                width="103"
                height="103.202"
                fill="white"
              />
            </clipPath>
          </defs>
        </svg>

        <h4 class="text-center text-[37px] font-semibold text-[#1D1D1D] mb-3">
          Success!
        </h4>

        <p class="text-center text-base text-[#3C4043] mb-6 w-full lg:w-[320px]">
          Your pages have been added to your project. You can access them in page management.
        </p>

        <a
          class="inline-flex items-center justify-center text-decoration-none hover:bg-[#6795EB] text-base font-semibold text-white !px-[11px] rounded bg-[#4F83E3] h-12 w-full lg:w-[180px]"
          :href="`/builder/${selectedProject.id}?page=${pageID}&import_pages=${selectedPages.join(',')}`"
        >
          Start Editing
        </a>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {mapState} from 'vuex'

export default {
  name: 'AddToExistingProject',
  props: {
    value: {
      type: Boolean,
      required: true
    },
    template: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      success: false,
      projects: [],
      selectedPages: [],
      selectedProject: null,
      pageID: null,
      pageQuery: 1,
      observer: null,
      limit: 20
    }
  },
  computed: {
    ...mapState('auth', {
      user: state => state.user
    }),
    disableSubmit () {
      return this.selectedProject === null || this.selectedPages.length === 0
    },
    modal: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    paginated () {
      return this.projects.slice(0, this.limit)
    },
    hasNextPage () {
      return this.paginated.length < this.projects.length
    }
  },
  methods: {
    addPage (page) {
      const index = this.selectedPages.indexOf(page.page_hash)
      if (index !== -1) {
        this.selectedPages.splice(index, 1)
      } else {
        this.selectedPages.push(page.page_hash)
      }
    },
    async fetchProjects () {
      const { data } = await axios.get(`/api/projects?user_id=${this.user.id}&crm=1&per_page=10&page=${this.pageQuery}`)
      const newProjects = data.data.data

      newProjects.forEach(project => {
        if (!this.projects.some(p => p.id === project.id)) {
          this.projects.push(project)
        }
      })

      if (newProjects.length < 10) {
        this.observer.disconnect()
      }
    },
    async importPagesToProject () {
      await this.$router.push(`/builder/${this.selectedProject.id}?import_pages=${this.selectedPages.join(',')}`)
    },
    async onOpen () {
      if (this.hasNextPage && this.observer) {
        this.$nextTick(() => {
          if (this.$refs.load) {
            this.observer.observe(this.$refs.load)
          }
        })
      }
    },
    onClose () {
      this.limit = 10
      if (this.observer) {
        this.observer.disconnect()
      }
    },
    async infiniteScroll ([entry]) {
      const { isIntersecting, target } = entry
      if (isIntersecting && target instanceof Element) {
        this.pageQuery += 1
        await this.fetchProjects()
      }
    }
  },
  created () {
    this.observer = new IntersectionObserver(this.infiniteScroll)
  },
  async mounted () {
    this.selectedProject = null
    this.selectedPages = []
    await this.fetchProjects()
  },
  beforeDestroy () {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }
}
</script>

<style lang="scss">
.add-to-project-dropdown {
  z-index: 1060 !important;
}
</style>
